import { useCallback, useEffect, useState } from "react";
import {
    createProductLot,
    deleteProductLot,
    getProductLots,
    updateProductLot
} from "@/api/admin.api.js";
import { useAuth } from "@/auth/AuthContext.jsx";
import { formatLotStatus, lotStatusClassName } from "@/utils/format.js";
import Price from "@/components/Price.jsx";
import { LOT_STATUSES } from "@/utils/constants.js";
import ConfirmDialog from "@/components/ConfirmDialog.jsx";
import RowActionsMenu from "@/components/RowActionsMenu.jsx";
import BackLink from "@/components/BackLink.jsx";
import { canDeleteLot, canSeeCostPrice, canWriteLot } from "@/utils/staffAccess.js";

const emptyLotForm = {
    name: "",
    description: "",
    costPrice: "0",
    quantity: "",
    receivedDate: new Date().toISOString().slice(0, 10),
    status: "READY"
};

function formatLotDate(value) {
    if (!value) {
        return "—";
    }
    const date = typeof value === "string" && value.length === 10
        ? new Date(`${value}T00:00:00`)
        : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleDateString("fr-FR");
}

function toLotForm(lot) {
    return {
        name: lot.name || "",
        description: lot.description || "",
        costPrice: lot.costPrice ?? "0",
        quantity: lot.remainingQuantity ?? "",
        receivedDate: lot.receivedDate || new Date().toISOString().slice(0, 10),
        status: lot.status || "READY"
    };
}

function isLotUnused(lot) {
    return lot
        && Number(lot.remainingQuantity) === Number(lot.initialQuantity)
        && lot.status !== "DELETED";
}

export default function AdminProductLots({ productId, onStockChanged, setMessage, setError }) {
    const { role } = useAuth();
    const showCostPrice = canSeeCostPrice(role);
    const allowDelete = canDeleteLot(role);
    const allowWriteLot = canWriteLot(role);
    const [lots, setLots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState("list");
    const [selectedLot, setSelectedLot] = useState(null);
    const [form, setForm] = useState(emptyLotForm);
    const [saving, setSaving] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState(null);

    const loadLots = useCallback(async () => {
        if (!productId) {
            setLots([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const response = await getProductLots(productId);
            setLots(response.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [productId, setError]);

    useEffect(() => {
        setViewMode("list");
        setSelectedLot(null);
        setForm({
            ...emptyLotForm,
            receivedDate: new Date().toISOString().slice(0, 10)
        });
        loadLots();
    }, [loadLots]);

    const openLotView = (lot) => {
        setSelectedLot(lot);
        setViewMode("view");
        setError("");
        setMessage("");
    };

    const startCreate = () => {
        setSelectedLot(null);
        setForm({
            ...emptyLotForm,
            receivedDate: new Date().toISOString().slice(0, 10)
        });
        setViewMode("create");
        setError("");
        setMessage("");
    };

    const startEdit = (lot) => {
        const target = lot || selectedLot;
        if (!target) {
            return;
        }
        setSelectedLot(target);
        setForm(toLotForm(target));
        setViewMode("edit");
        setError("");
        setMessage("");
    };

    const backToList = () => {
        setViewMode("list");
        setSelectedLot(null);
        setForm({
            ...emptyLotForm,
            receivedDate: new Date().toISOString().slice(0, 10)
        });
        setError("");
    };

    const cancelForm = () => {
        if (selectedLot && viewMode === "edit") {
            setViewMode("view");
            setForm(toLotForm(selectedLot));
        } else {
            backToList();
        }
        setError("");
    };

    const refreshAfterMutation = async (nextLotId = null) => {
        const response = await getProductLots(productId);
        const nextLots = response.data || [];
        setLots(nextLots);
        if (onStockChanged) {
            await onStockChanged();
        }
        if (nextLotId) {
            const next = nextLots.find((lot) => lot.id === nextLotId);
            if (next) {
                setSelectedLot(next);
                setViewMode("view");
                return;
            }
        }
        backToList();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!productId) {
            return;
        }
        setSaving(true);
        setMessage("");
        setError("");
        try {
            if (viewMode === "edit" && selectedLot) {
                await updateProductLot(productId, selectedLot.id, {
                    name: form.name.trim(),
                    description: form.description.trim() || undefined,
                    ...(showCostPrice
                        ? { costPrice: form.costPrice === "" || form.costPrice == null ? 0 : Number(form.costPrice) }
                        : {}),
                    remainingQuantity: Number(form.quantity),
                    receivedDate: form.receivedDate || undefined,
                    status: form.status
                });
                setMessage("Lot mis à jour");
                await refreshAfterMutation(selectedLot.id);
            } else {
                const created = await createProductLot(productId, {
                    name: form.name.trim(),
                    description: form.description.trim() || undefined,
                    ...(showCostPrice
                        ? { costPrice: form.costPrice === "" || form.costPrice == null ? 0 : Number(form.costPrice) }
                        : {}),
                    quantity: Number(form.quantity),
                    receivedDate: form.receivedDate || undefined,
                    status: form.status
                });
                setMessage("Lot ajouté");
                await refreshAfterMutation(created.data?.id || null);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!productId || !pendingDeleteId) {
            return;
        }
        const deletedId = pendingDeleteId;
        try {
            await deleteProductLot(productId, deletedId);
            setPendingDeleteId(null);
            setMessage("Lot supprimé");
            await loadLots();
            if (onStockChanged) {
                await onStockChanged();
            }
            if (selectedLot?.id === deletedId) {
                backToList();
            }
        } catch (err) {
            setPendingDeleteId(null);
            setError(err.message);
        }
    };

    const buildLotMenuItems = (lot, { includeView = true } = {}) => {
        const items = [];
        if (includeView) {
            items.push({
                id: "view",
                label: "Voir le détail",
                onClick: () => openLotView(lot)
            });
        }
        if (allowWriteLot) {
            items.push({
                id: "edit",
                label: "Modifier",
                onClick: () => startEdit(lot)
            });
        }
        if (allowDelete && isLotUnused(lot)) {
            items.push({
                id: "delete",
                label: "Supprimer",
                danger: true,
                onClick: () => setPendingDeleteId(lot.id)
            });
        }
        return items;
    };

    if (!productId) {
        return (
            <p className="muted">
                Enregistrez d&apos;abord le produit pour gérer les lots
                {showCostPrice ? " (prix de revient)" : ""}.
            </p>
        );
    }

    const pendingDelete = lots.find((lot) => lot.id === pendingDeleteId)
        || (selectedLot?.id === pendingDeleteId ? selectedLot : null);

    const deleteConfirmDialog = (
        <ConfirmDialog
            open={!!pendingDeleteId}
            title="Supprimer le lot"
            message={
                pendingDelete
                    ? `Voulez-vous vraiment supprimer le lot « ${pendingDelete.name} » ?`
                    : "Voulez-vous vraiment supprimer ce lot ?"
            }
            confirmLabel="Supprimer"
            dangerous
            onConfirm={handleDelete}
            onCancel={() => setPendingDeleteId(null)}
        />
    );

    if (viewMode === "create" || viewMode === "edit") {
        return (
            <div className="admin-product-lots">
                <div className="section-header">
                    <h3>{viewMode === "edit" ? "Modifier le lot" : "Ajouter un lot"}</h3>
                </div>
                <form className="admin-lot-form" onSubmit={handleSubmit}>
                    <label className="field">
                        Nom
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            required
                            maxLength={255}
                        />
                    </label>
                    <label className="field">
                        Description
                        <textarea
                            rows="2"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            maxLength={2000}
                        />
                    </label>
                    {showCostPrice && (
                        <label className="field field--amount">
                            Prix de revient (FCFA)
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.costPrice}
                                onChange={(e) => setForm({ ...form, costPrice: e.target.value })}
                            />
                        </label>
                    )}
                    <label className="field field--qty">
                        {viewMode === "edit" ? "Quantité restante" : "Quantité"}
                        <input
                            type="number"
                            min={viewMode === "edit" ? "0" : "1"}
                            step="1"
                            value={form.quantity}
                            onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                            required
                        />
                    </label>
                    <label className="field field--date">
                        Date de réception
                        <input
                            type="date"
                            value={form.receivedDate}
                            onChange={(e) => setForm({ ...form, receivedDate: e.target.value })}
                            required
                        />
                    </label>
                    <label className="field field--status">
                        Statut
                        <select
                            value={form.status}
                            onChange={(e) => setForm({ ...form, status: e.target.value })}
                        >
                            {LOT_STATUSES.filter((status) => status !== "DELETED" || viewMode === "edit").map((status) => (
                                <option key={status} value={status}>{formatLotStatus(status)}</option>
                            ))}
                        </select>
                    </label>
                    <div className="profile-form-actions">
                        <button className="btn btn-primary btn-inline" type="submit" disabled={saving}>
                            {saving
                                ? "Enregistrement..."
                                : (viewMode === "edit" ? "Enregistrer" : "Ajouter le lot")}
                        </button>
                        <button type="button" className="btn btn-secondary btn-inline" onClick={cancelForm}>
                            Annuler
                        </button>
                    </div>
                </form>
                {deleteConfirmDialog}
            </div>
        );
    }

    if (viewMode === "view" && selectedLot) {
        return (
            <div className="admin-product-lots">
                <div className="section-header">
                    <div>
                        <BackLink onClick={backToList} className="purchase-detail-back">
                            Retour aux lots
                        </BackLink>
                        <h3>Détails du lot</h3>
                    </div>
                    <RowActionsMenu
                        ariaLabel={`Actions pour ${selectedLot.name}`}
                        items={buildLotMenuItems(selectedLot, { includeView: false })}
                    />
                </div>

                <dl className="profile-detail-view profile-detail-view--compact">
                    <div className="profile-detail-row">
                        <dt>Nom</dt>
                        <dd>{selectedLot.name}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Description</dt>
                        <dd>{selectedLot.description || "—"}</dd>
                    </div>
                    {showCostPrice && (
                        <div className="profile-detail-row">
                            <dt>Prix de revient</dt>
                            <dd><Price value={selectedLot.costPrice ?? 0} /></dd>
                        </div>
                    )}
                    <div className="profile-detail-row">
                        <dt>Quantité initiale</dt>
                        <dd>{selectedLot.initialQuantity}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Quantité restante</dt>
                        <dd>{selectedLot.remainingQuantity}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Date de réception</dt>
                        <dd>{formatLotDate(selectedLot.receivedDate)}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Statut</dt>
                        <dd>
                            <span className={lotStatusClassName(selectedLot.status)}>
                                {formatLotStatus(selectedLot.status)}
                            </span>
                        </dd>
                    </div>
                </dl>
                {deleteConfirmDialog}
            </div>
        );
    }

    return (
        <div className="admin-product-lots">
            <div className="section-header catalog-results-header-main">
                <div>
                    <h3>{showCostPrice ? "Lots / stock (prix de revient)" : "Lots / stock"}</h3>
                    <p className="muted admin-list-hint">
                        {allowWriteLot
                            ? "Chaque arrivage est un lot. Cliquez sur une ligne ou le menu ⋮ pour voir le détail."
                            : "Consultation seule — le stock se met à jour lorsque vous terminez une commande."}
                    </p>
                </div>
                {allowWriteLot && (
                    <button type="button" className="btn btn-primary btn-inline" onClick={startCreate}>
                        Ajouter un lot
                    </button>
                )}
            </div>

            {loading && <p className="muted">Chargement des lots...</p>}

            {!loading && lots.length === 0 && (
                <p className="muted">Aucun lot pour ce produit.</p>
            )}

            {!loading && lots.length > 0 && (
                <div className="admin-table">
                    {lots.map((lot) => (
                        <div key={lot.id} className="admin-row admin-list-row">
                            <button
                                type="button"
                                className="admin-list-row-main"
                                onClick={() => openLotView(lot)}
                            >
                                <div className="admin-row-body">
                                    <strong>{lot.name}</strong>
                                    <p className="muted">
                                        <span className={lotStatusClassName(lot.status)}>
                                            {formatLotStatus(lot.status)}
                                        </span>
                                        {" · "}
                                        Reçu le {formatLotDate(lot.receivedDate)}
                                        {" · "}
                                        {lot.remainingQuantity} article{lot.remainingQuantity === 1 ? "" : "s"} restant{lot.remainingQuantity === 1 ? "" : "s"}
                                        {" / "}
                                        {lot.initialQuantity}
                                        {showCostPrice && (
                                            <>
                                                {" · "}
                                                prix de revient <Price value={lot.costPrice ?? 0} />
                                            </>
                                        )}
                                    </p>
                                </div>
                                <span className="admin-list-row-chevron" aria-hidden="true">›</span>
                            </button>
                            <RowActionsMenu
                                ariaLabel={`Actions pour ${lot.name}`}
                                items={buildLotMenuItems(lot)}
                            />
                        </div>
                    ))}
                </div>
            )}
            {deleteConfirmDialog}
        </div>
    );
}
